import cx from 'classnames';
import { motion } from 'motion/react';
import { ChangeEvent, KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { IconCross } from '../../../design-system/components/icons/Cross';
import { IconSearch } from '../../../design-system/components/icons/Search';
import { usePrefersReducedMotion } from '../../../shared/hooks/usePrefersReducedMotion';
import { SearchOverlayOrigin } from '../SearchOverlay.types';

import style from './SearchInput.module.css';

const ORIGIN_TO_LAYOUT_ID: Partial<Record<SearchOverlayOrigin, string>> = {
    cover: 'search-bar-cover',
};

type Props = {
    origin: SearchOverlayOrigin;
    value: string;
    onChange: (value: string) => void;
    onClose: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    listboxId?: string;
    isExpanded?: boolean;
    activeOptionId?: string;
};

export function SearchInput({
    origin,
    value,
    onChange,
    onClose,
    onKeyDown,
    listboxId,
    isExpanded = false,
    activeOptionId,
}: Props) {
    const { formatMessage } = useIntl();
    const reducedMotion = usePrefersReducedMotion();
    const layoutId = reducedMotion ? undefined : ORIGIN_TO_LAYOUT_ID[origin];
    const iconLayoutId = layoutId ? `${layoutId}-icon` : undefined;

    return (
        <search className={style.header}>
            <motion.div
                className={style.searchbar}
                layoutId={layoutId}
                style={{
                    backgroundColor: 'var(--color-neutral-100)',
                    boxShadow: 'inset 0 0 0 1.5px var(--color-neutral-200)',
                }}
            >
                <motion.div className={style.searchIcon} layoutId={iconLayoutId}>
                    <IconSearch />
                </motion.div>
                <input
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: overlay opens to let the user type immediately
                    autoFocus
                    type="search"
                    name="search"
                    inputMode="search"
                    enterKeyHint="search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls={listboxId}
                    aria-expanded={isExpanded}
                    aria-activedescendant={activeOptionId}
                    placeholder={formatMessage({ id: 'page.home.searchPlaceholder' })}
                    className={cx(style.input, 'font-body-regular')}
                    value={value}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-form-type="other"
                    maxLength={100}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onChange(event.target.value)
                    }
                    onKeyDown={onKeyDown}
                />
            </motion.div>
            <button
                className={style.closeButton}
                onClick={onClose}
                aria-label={formatMessage({ id: 'global.search.overlay.closeLabel' })}
            >
                <IconCross className={style.closeIcon} />
            </button>
        </search>
    );
}
